// The desk capture (UC-GOVERNANCE-013 A3; BR-GOVERNANCE-020, -026; D64): consent recorded by the staff member in their own
// session, on the patient's Consents tab (`?record=desk`), for a patient who signs on the desk's screen, brings or signs a
// paper form, or consents in person or by telephone. The screen reads the consent form (`GET /patients/{id}/consents/form`)
// and shows the consent text in force in full with the six purposes ticked as the Owner's defaults; the staff member says how
// the patient consented — that is the record's channel — and gives the evidence the channel needs: the signature drawn on the
// pad (`InStoreTablet`), the scan of the signed form (`PaperScan`, prepared as a document upload is: a photo over 2000 px or
// 2 MB shrunk on the device, a PDF up to 2 MB),
// the confirmation that the signed form is filed (`Paper`), or the attestation that they heard the patient (`Verbal`); the
// record names them as its capturing user, the witness. A patient under 18 is signed for by a parent or guardian, defaulted to
// the main account holder (PAT-73). One `POST /patients/{id}/consents` with an Idempotency-Key per attempt; replaced wording
// (`409 VERSION_CONFLICT`) re-reads the form and asks again; a refusal lands on its field and keeps the answers.
import {
  CONSENT_PURPOSES, ConsentInputSchema, FORM_SCAN_CONTENT_TYPES, controlIdDisplay,
  type ConsentChannel, type ConsentFormView, type ConsentInput, type ConsentPurpose, type ConsentRecordView, type FormScan, type GuardianRelationship, type PatientView,
} from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { newIdempotencyKey } from "../api/client";
import { consent as consentApi } from "../api/endpoints.consent";
import { describeError, isApiError } from "../api/problem";
import { GUARDIAN_RELATIONSHIPS, GUARDIAN_RELATIONSHIP_LABELS } from "../kiosk/form";
import { SignaturePad, type SignaturePadHandle } from "../kiosk/SignaturePad";
import { formatDate } from "../lib/format";
import { useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, Chip, ErrorAlert, Field, Loading, Panel, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { prepareUpload } from "./PatientsDocuments";
import { patientName } from "./PatientsShared";

/** How the patient consented at the desk, and the channel the record carries (BR-GOVERNANCE-026). */
export type DeskHow = "pad" | "scan" | "filed" | "verbal";
export const DESK_CHANNEL: Record<DeskHow, ConsentChannel> = { pad: "InStoreTablet", scan: "PaperScan", filed: "Paper", verbal: "Verbal" };
const HOW_LABEL: Record<DeskHow, string> = {
  pad: "Signed on this screen",
  scan: "Signed a paper form — attach the scan",
  filed: "Signed a paper form — filed, not scanned",
  verbal: "Gave consent verbally — in person or by telephone",
};
const HOWS: readonly DeskHow[] = ["pad", "scan", "filed", "verbal"];

type Errors = Record<string, string>;
const localDay = (d: string): string => formatDate(`${d}T00:00:00.000Z`);
const isFormScanType = (t: string): t is FormScan["contentType"] => (FORM_SCAN_CONTENT_TYPES as readonly string[]).includes(t);

/**
 * The file as the capture's `formScan`, prepared on the device exactly as the Documents tab prepares an upload (PAT-26): HEIC
 * refused with the note, a PDF over 2 MB refused, an image over 2 000 px or 2 MB shrunk to a JPEG, anything else sent as it is.
 * A scan is a JPEG, PNG or PDF (BR-GOVERNANCE-026), so a WebP the shrink left alone is refused here rather than by the server.
 */
export async function prepareScan(file: File): Promise<{ ok: true; scan: FormScan } | { ok: false; error: string }> {
  const prepared = await prepareUpload(file);
  if (!prepared.ok) return prepared;
  const { contentType, data } = prepared.file;
  if (!isFormScanType(contentType)) return { ok: false, error: `${file.type || file.name} is not accepted — attach the scan as a PDF, JPEG or PNG.` };
  return { ok: true, scan: { contentType, data } };
}

/** A schema or server field path → the field the screen shows it under. */
function fieldOf(path: string): string {
  if (path.startsWith("formScan")) return "scan";
  if (path.startsWith("signatureImage")) return "signature";
  if (path.startsWith("guardian.relationship")) return "guardianRelationship";
  if (path.startsWith("guardian.idNumber")) return "guardianIdNumber";
  if (path.startsWith("guardian")) return "guardianIdNumber";
  if (path.startsWith("signerName")) return "signerName";
  if (path.startsWith("signerType")) return "signerName";
  if (path.startsWith("purposes")) return "purposes";
  if (path.startsWith("channel")) return "how";
  return "form";
}
/** The order the fields appear in, for focusing the first one refused. */
const FIELD_ORDER = ["purposes", "how", "signature", "scan", "filed", "verbal", "signerName", "guardianRelationship", "guardianIdNumber"];
const FIELD_ID: Record<string, string> = {
  purposes: "cd-purposes", how: "cd-how-pad", signature: "cd-signature", scan: "cd-scan", filed: "cd-filed-field", verbal: "cd-verbal-field",
  signerName: "cd-signer-name", guardianRelationship: "cd-guardian-relationship", guardianIdNumber: "cd-guardian-id",
};
/** The tick boxes wear the prototype's box: focus the real control inside its field, so Space answers it at once. */
function focusField(key: string): void {
  const el = document.getElementById(FIELD_ID[key] ?? "");
  const inner = key === "filed" || key === "verbal" ? el?.querySelector<HTMLInputElement>("input[type=checkbox]") : null;
  (inner ?? el)?.focus();
}

interface Draft {
  how: DeskHow | null;
  granted: Record<ConsentPurpose, boolean>;
  signerName: string;
  guardianRelationship: GuardianRelationship | "";
  guardianIdNumber: string;
  guardianPatientId: string | null;
  signed: boolean;
  scanFile: File | null;
  filedConfirmed: boolean;
  verbalAttested: boolean;
}

function draftOf(f: ConsentFormView, fallbackName: string): Draft {
  const guardian = f.patient.isMinor ? f.patient.guardianDefault : null;
  const relationship = guardian?.relationship && (GUARDIAN_RELATIONSHIPS as readonly string[]).includes(guardian.relationship) ? (guardian.relationship as GuardianRelationship) : "";
  return {
    how: null,
    granted: Object.fromEntries(CONSENT_PURPOSES.map((p) => { const d = f.purposes.find((x) => x.purpose === p); return [p, !!d && (d.required || d.preTicked)]; })) as Record<ConsentPurpose, boolean>,
    signerName: f.patient.isMinor ? guardian?.name ?? "" : f.patient.displayName || fallbackName,
    guardianRelationship: relationship, guardianIdNumber: "", guardianPatientId: guardian?.patientId ?? null,
    signed: false, scanFile: null, filedConfirmed: false, verbalAttested: false,
  };
}

/**
 * The desk capture panel. `onDone` receives the recorded consent (the list is read again); `onCancel` returns to the list.
 * Mounted only for a role holding `governance.consent.write` (the Consents tab decides).
 */
export function ConsentDeskCapture({ p, onDone, onCancel }: { p: PatientView; onDone: (r: ConsentRecordView) => void; onCancel: () => void }) {
  const sess = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const pad = useRef<SignaturePadHandle>(null);
  const scanInput = useRef<HTMLInputElement>(null);
  const q = useQuery({ queryKey: ["patient", p.id, "consent-form"], queryFn: () => consentApi.form(p.id), staleTime: 0 });
  const form = q.data?.data ?? null;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ReactNode | null>(null);
  // The form read that the draft was built from: a re-read after replaced wording starts the answers again.
  const builtFor = useRef<string | null>(null);

  useEffect(() => {
    if (!form) return;
    const stamp = `${form.consentText?.version ?? "none"}`;
    if (builtFor.current === stamp) return;
    builtFor.current = stamp;
    pad.current?.clear();
    if (scanInput.current) scanInput.current.value = "";
    setDraft(draftOf(form, patientName(p)));
    setErrors({});
  }, [form, p]);

  const set = (patch: Partial<Draft>) => { setDraft((d) => (d ? { ...d, ...patch } : d)); setProblem(null); };
  const clearError = (...keys: string[]) => setErrors((e) => { if (!keys.some((k) => e[k])) return e; const n = { ...e }; for (const k of keys) delete n[k]; return n; });
  const me = sess ? `${sess.user.displayName} ${controlIdDisplay(sess.user.controlId)}` : "you";

  if (q.isPending) return <Panel title="Record consent at the desk"><Loading what="the consent form" /></Panel>;
  if (q.isError) return <Panel title="Record consent at the desk" foot={<button className="btn" type="button" onClick={onCancel}>Back to the consent records</button>}><ErrorAlert error={q.error} /></Panel>;
  if (!form || !draft) return null;
  const text = form.consentText;
  if (!text) {
    return (
      <Panel title="Record consent at the desk" foot={<button className="btn" type="button" onClick={onCancel}>Back to the consent records</button>}>
        <Alert tone="warn"><b>No consent text has been published.</b> Consent is recorded against the wording in force; the Owner publishes it under Settings › General › Consent text.</Alert>
      </Panel>
    );
  }
  const minor = form.patient.isMinor;
  const signer = draft.signerName.trim() || (minor ? "the parent or guardian" : patientName(p));

  const validate = (): Errors => {
    const e: Errors = {};
    if (!draft.how) e.how = "Choose how the patient consented.";
    if (draft.how === "pad" && (!draft.signed || pad.current?.isEmpty())) e.signature = "The signature is required: ask the patient to sign on the screen.";
    if (draft.how === "scan" && !draft.scanFile) e.scan = "Attach the scan or a photo of the signed form.";
    if (draft.how === "filed" && !draft.filedConfirmed) e.filed = "Confirm that the signed form is filed at the practice.";
    if (draft.how === "verbal" && !draft.verbalAttested) e.verbal = "Confirm that you heard the patient give these answers.";
    if (minor) {
      if (!draft.signerName.trim()) e.signerName = "The parent or guardian's full name is required.";
      if (!draft.guardianRelationship) e.guardianRelationship = "Choose the relationship.";
      if (!draft.guardianIdNumber.trim()) e.guardianIdNumber = "The parent or guardian's ID or passport number is required.";
    }
    return e;
  };
  const focusFirst = (e: Errors) => {
    const first = FIELD_ORDER.find((k) => e[k]);
    if (first) setTimeout(() => focusField(first), 0);
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (busy) return;
    setProblem(null);
    const local = validate();
    if (Object.keys(local).length > 0) { setErrors(local); focusFirst(local); return; }
    const how = draft.how!;
    setBusy(true);
    try {
      let formScan: FormScan | undefined;
      if (how === "scan" && draft.scanFile) {
        const prepared = await prepareScan(draft.scanFile);
        if (!prepared.ok) { const e = { scan: prepared.error }; setErrors(e); focusFirst(e); return; }
        formScan = prepared.scan;
      }
      const png = how === "pad" ? pad.current?.toPng() ?? null : null;
      const body = {
        textVersion: text.version,
        purposes: CONSENT_PURPOSES.map((purpose) => ({ purpose, granted: purpose === "CareAndRecords" ? true : draft.granted[purpose] })),
        channel: DESK_CHANNEL[how],
        signerType: minor ? "Guardian" : "Self",
        signerName: draft.signerName.trim() || undefined,
        guardian: minor ? { relationship: draft.guardianRelationship || undefined, idNumber: draft.guardianIdNumber.trim(), patientId: draft.guardianPatientId ?? undefined } : undefined,
        signatureImage: png ? { contentType: "image/png", data: png } : undefined,
        formScan,
      };
      const parsed = ConsentInputSchema.safeParse(body);
      if (!parsed.success) {
        const e: Errors = {};
        for (const i of parsed.error.issues) { const k = fieldOf(i.path.map(String).join(".")); e[k] = e[k] ?? i.message; }
        setErrors(e);
        focusFirst(e);
        return;
      }
      const { data } = await consentApi.capture(p.id, parsed.data as ConsentInput, newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: ["patient", p.id] });
      void qc.invalidateQueries({ queryKey: ["patients"] });
      onDone(data);
    } catch (err) {
      if (isApiError(err) && err.code === "VERSION_CONFLICT") {
        // The wording changed while the form was open (UC-GOVERNANCE-013 E1): read it again and ask the patient again.
        builtFor.current = null;
        void q.refetch();
        setProblem(<Alert tone="warn"><b>The consent wording changed while this was open.</b> {typeof err.meta.currentTextVersion === "number" ? `Version ${err.meta.currentTextVersion} is now in force. ` : ""}Read the new wording to the patient and record their answers again.</Alert>);
      } else if (isApiError(err) && err.code === "VALIDATION_FAILED" && err.errors.length > 0) {
        const e: Errors = {};
        for (const f of err.errors) { const k = fieldOf(f.path); e[k] = e[k] ?? f.message; }
        setErrors(e);
        focusFirst(e);
        if (e.form) setProblem(<Alert tone="crit"><b>Not recorded.</b> {e.form}</Alert>);
      } else {
        setProblem(<Alert tone="crit"><b>Not recorded.</b> {describeError(err)}</Alert>);
      }
    } finally {
      setBusy(false);
    }
  };

  const blocked = block ? WRITE_BLOCK_TITLE[block] : undefined;
  return (
    <Panel title="Record consent at the desk" meta={`consent text v${text.version} · in force from ${localDay(text.effectiveFrom)}`} className="consent-desk">
      <form id="consent-desk" className="stack" noValidate onSubmit={(e) => void submit(e)}>
        {problem}
        {form.patient.consentStatus === "Outdated" && form.patient.lastSigned && <Alert tone="info"><b>Last signed on version {form.patient.lastSigned.textVersion}, {formatDate(form.patient.lastSigned.signedAt)}.</b> Version {text.version} has replaced that wording; this records the patient's answers to it.</Alert>}
        <div className="split wide">
          <div className="stack">
            <div className="field">
              <label htmlFor="cd-text">Consent text v{text.version}</label>
              <div id="cd-text" className="input ta consent-text scroll" tabIndex={0} role="document" aria-label={`Consent text version ${text.version}`}>{text.bodyText}</div>
              <span className="help">Show the patient this wording, or read it to them, before they answer.{form.privacyContactEmail.value ? ` Questions and withdrawals: ${form.privacyContactEmail.value}.` : ""}</span>
            </div>
            <fieldset className="stack gap-8" id="cd-purposes" tabIndex={-1} aria-describedby={errors.purposes ? "cd-purposes-error" : undefined}>
              <legend className="small strong muted">The patient's answers</legend>
              {CONSENT_PURPOSES.map((code) => {
                const purpose = form.purposes.find((x) => x.purpose === code);
                if (!purpose) return null;
                return (
                  <div key={code} className="stack gap-2 cd-purpose">
                    <Checkbox name={`purpose-${code}`} checked={purpose.required || draft.granted[code]} disabled={purpose.required} onChange={(on) => { set({ granted: { ...draft.granted, [code]: on } }); clearError("purposes"); }}>
                      <span>{purpose.label}{purpose.required && <> <Chip tone="neutral">Required</Chip></>}</span>
                    </Checkbox>
                    <span className="help cd-desc">{purpose.description}</span>
                  </div>
                );
              })}
              {errors.purposes && <div className="error" id="cd-purposes-error" role="alert">{errors.purposes}</div>}
            </fieldset>
          </div>
          <div className="stack">
            <fieldset className="stack gap-8" aria-describedby={errors.how ? "cd-how-error" : undefined}>
              <legend className="small strong muted">How the patient consented</legend>
              {HOWS.map((h) => (
                <label key={h} className="check">
                  <input type="radio" id={`cd-how-${h}`} name="consent-how" checked={draft.how === h} onChange={() => { set({ how: h }); clearError("how", "signature", "scan", "filed", "verbal"); }} />
                  <span className={`box radio${draft.how === h ? " on" : ""}`} />{HOW_LABEL[h]}
                </label>
              ))}
              {errors.how && <div className="error" id="cd-how-error" role="alert">{errors.how}</div>}
            </fieldset>

            {draft.how === "pad" && (
              <div className="field">
                <label id="cd-signature-label">{minor ? "Parent or guardian's signature" : "Patient's signature"} <span className="req">*</span></label>
                <div id="cd-signature" tabIndex={-1}>
                  <SignaturePad ref={pad} onChange={(empty) => { set({ signed: !empty }); if (!empty) clearError("signature"); }} caption={`Signed by ${signer}`} />
                </div>
                {errors.signature ? <div className="error" role="alert">{errors.signature}</div> : <span className="help">Turn the screen to the patient to sign with a finger or stylus; the signature is stored with this consent.</span>}
              </div>
            )}
            {draft.how === "scan" && (
              <Field label="Scan of the signed form" required htmlFor="cd-scan" error={errors.scan} help="A PDF up to 2 MB, or a photo — reduced on this device to 2 000 px. It is filed as the patient's POPIA consent document.">
                <input id="cd-scan" ref={scanInput} type="file" accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png" onChange={(e) => { set({ scanFile: e.target.files?.[0] ?? null }); clearError("scan"); }} />
              </Field>
            )}
            {draft.how === "filed" && (
              <div className="field" id="cd-filed-field">
                <Checkbox name="cd-filed" checked={draft.filedConfirmed} onChange={(on) => { set({ filedConfirmed: on }); clearError("filed"); }}>The signed form is filed at the practice</Checkbox>
                {errors.filed ? <div className="error" role="alert">{errors.filed}</div> : <span className="help">No scan is kept with the record; attach one instead when you can.</span>}
              </div>
            )}
            {draft.how === "verbal" && (
              <div className="field" id="cd-verbal-field">
                <Checkbox name="cd-verbal" checked={draft.verbalAttested} onChange={(on) => { set({ verbalAttested: on }); clearError("verbal"); }}>I heard {signer} give these answers, in person or by telephone</Checkbox>
                {errors.verbal ? <div className="error" role="alert">{errors.verbal}</div> : <span className="help">You are recorded as the witness: {me}. No signature is kept.</span>}
              </div>
            )}

            {minor ? (
              <div className="stack gap-8">
                <Alert tone="info"><b>A parent or guardian consents</b> for a patient under 18{form.patient.guardianDefault ? `; the main member, ${form.patient.guardianDefault.name}, is filled in` : ""}.</Alert>
                <Field label="Parent or guardian's full name" required htmlFor="cd-signer-name" error={errors.signerName}>
                  <TextInput id="cd-signer-name" value={draft.signerName} maxLength={200} autoComplete="off" error={!!errors.signerName} onChange={(e) => { set({ signerName: e.target.value }); clearError("signerName"); }} />
                </Field>
                <div className="grid-2">
                  <Field label="Relationship" required htmlFor="cd-guardian-relationship" error={errors.guardianRelationship}>
                    <Select id="cd-guardian-relationship" value={draft.guardianRelationship} onChange={(e) => { set({ guardianRelationship: e.target.value as GuardianRelationship | "" }); clearError("guardianRelationship"); }}>
                      <option value="">—</option>
                      {GUARDIAN_RELATIONSHIPS.map((r) => <option key={r} value={r}>{GUARDIAN_RELATIONSHIP_LABELS[r]}</option>)}
                    </Select>
                  </Field>
                  <Field label="ID or passport number" required htmlFor="cd-guardian-id" error={errors.guardianIdNumber}>
                    <TextInput id="cd-guardian-id" mono value={draft.guardianIdNumber} maxLength={32} autoComplete="off" error={!!errors.guardianIdNumber} onChange={(e) => { set({ guardianIdNumber: e.target.value }); clearError("guardianIdNumber"); }} />
                  </Field>
                </div>
              </div>
            ) : (
              <Field label="Name as signed" htmlFor="cd-signer-name" error={errors.signerName} help="The patient's own name unless they sign otherwise.">
                <TextInput id="cd-signer-name" value={draft.signerName} maxLength={200} autoComplete="off" error={!!errors.signerName} onChange={(e) => { set({ signerName: e.target.value }); clearError("signerName"); }} />
              </Field>
            )}
          </div>
        </div>
        <div className="row c gap-8">
          <button className="btn primary" type="submit" disabled={busy || block !== null} title={blocked}><Icon name="check" />{busy ? "Recording…" : "Record consent"}</button>
          <button className="btn" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <span className="help right">Recorded by {me} · {patientName(p)} <span className="mono">{p.accountCode}</span></span>
        </div>
      </form>
    </Panel>
  );
}
